import type { GameSettings, RegionId } from "../types/game";

type SoundName = "click" | "build" | "upgrade" | "sell" | "arrow" | "magic" | "blast" | "sword" | "hit" | "spell" | "wave" | "victory" | "defeat" | "boss" | "error";

const regionRoots: Record<RegionId, number> = { sunmeadow: 220, elderwild: 196, suncleft: 174.61, gloamfen: 164.81, frostcrown: 246.94, cinderfast: 146.83, starless: 138.59 };

export class AudioManager {
  private context?: AudioContext;
  private master?: GainNode;
  private music?: GainNode;
  private effects?: GainNode;
  private timer?: number;
  private step = 0;
  private settings: GameSettings;
  private region: RegionId = "sunmeadow";
  private intensity = 0;

  constructor(settings: GameSettings) {
    this.settings = settings;
  }

  async unlock(): Promise<void> {
    if (!this.context) {
      this.context = new AudioContext();
      this.master = this.context.createGain();
      this.music = this.context.createGain();
      this.effects = this.context.createGain();
      this.music.connect(this.master);
      this.effects.connect(this.master);
      this.master.connect(this.context.destination);
      this.applySettings(this.settings);
      this.startScore();
    }
    if (this.context.state === "suspended") await this.context.resume();
  }

  applySettings(settings: GameSettings): void {
    this.settings = settings;
    if (!this.context || !this.master || !this.music || !this.effects) return;
    const now = this.context.currentTime;
    this.master.gain.setTargetAtTime(settings.masterVolume, now, 0.03);
    this.music.gain.setTargetAtTime(settings.musicVolume * 0.34, now, 0.03);
    this.effects.gain.setTargetAtTime(settings.effectsVolume * 0.7, now, 0.03);
  }

  setRegion(region: RegionId): void { this.region = region; }
  setIntensity(value: number): void { this.intensity = Math.max(0, Math.min(2, value)); }

  private startScore(): void {
    if (this.timer) window.clearInterval(this.timer);
    this.timer = window.setInterval(() => {
      if (!this.context || this.context.state !== "running" || !this.music) return;
      const root = regionRoots[this.region];
      const scale = [1, 1.125, 1.25, 1.5, 1.6875, 2];
      const frequency = root * scale[this.step % scale.length] * (this.step % 8 === 0 ? 0.5 : 1);
      this.tone(frequency, 0.42, this.intensity > 0 ? "triangle" : "sine", this.music, 0.045 + this.intensity * 0.015);
      if (this.intensity > 0 && this.step % 2 === 0) this.tone(root * 0.5, 0.12, "square", this.music, 0.018);
      if (this.intensity > 1 && this.step % 4 === 0) this.noise(0.06, this.music, 0.018);
      this.step += 1;
    }, 420);
  }

  private tone(frequency: number, duration: number, type: OscillatorType, destination: AudioNode, gainValue: number, slide = 0): void {
    if (!this.context) return;
    const oscillator = this.context.createOscillator();
    const gain = this.context.createGain();
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(frequency, this.context.currentTime);
    if (slide) oscillator.frequency.exponentialRampToValueAtTime(Math.max(30, frequency + slide), this.context.currentTime + duration);
    gain.gain.setValueAtTime(0.0001, this.context.currentTime);
    gain.gain.exponentialRampToValueAtTime(Math.max(0.0002, gainValue), this.context.currentTime + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, this.context.currentTime + duration);
    oscillator.connect(gain);
    gain.connect(destination);
    oscillator.start();
    oscillator.stop(this.context.currentTime + duration + 0.02);
  }

  private noise(duration: number, destination: AudioNode, gainValue: number): void {
    if (!this.context) return;
    const length = Math.floor(this.context.sampleRate * duration);
    const buffer = this.context.createBuffer(1, length, this.context.sampleRate);
    const data = buffer.getChannelData(0);
    for (let index = 0; index < length; index += 1) data[index] = Math.random() * 2 - 1;
    const source = this.context.createBufferSource();
    const filter = this.context.createBiquadFilter();
    const gain = this.context.createGain();
    source.buffer = buffer;
    filter.type = "lowpass";
    filter.frequency.value = 900;
    gain.gain.value = gainValue;
    source.connect(filter);
    filter.connect(gain);
    gain.connect(destination);
    source.start();
  }

  play(name: SoundName, pan = 0): void {
    if (!this.context || !this.effects || this.context.state !== "running") return;
    const panner = this.context.createStereoPanner();
    panner.pan.value = Math.max(-1, Math.min(1, pan));
    panner.connect(this.effects);
    const specs: Record<SoundName, [number, number, OscillatorType, number, number]> = {
      click: [480, 0.06, "sine", 0.06, 90], build: [180, 0.18, "triangle", 0.1, 180], upgrade: [330, 0.28, "sine", 0.1, 440], sell: [520, 0.15, "triangle", 0.08, -180], arrow: [680, 0.05, "triangle", 0.035, -220], magic: [280, 0.2, "sine", 0.055, 520], blast: [80, 0.22, "sawtooth", 0.08, -30], sword: [320, 0.07, "square", 0.035, -120], hit: [120, 0.05, "triangle", 0.03, -40], spell: [220, 0.5, "sine", 0.12, 880], wave: [260, 0.34, "square", 0.07, 250], victory: [330, 0.9, "triangle", 0.12, 660], defeat: [196, 0.85, "sawtooth", 0.08, -120], boss: [92, 0.7, "square", 0.1, 50], error: [110, 0.12, "square", 0.06, -30],
    };
    const [frequency, duration, type, gain, slide] = specs[name];
    this.tone(frequency, duration, type, panner, gain, slide);
    if (name === "blast" || name === "boss") this.noise(duration * 0.7, panner, gain * 0.6);
  }

  dispose(): void {
    if (this.timer) window.clearInterval(this.timer);
    void this.context?.close();
    this.context = undefined;
  }
}
